import { useCallback, useEffect, useState } from 'react';
import { apiGet } from '../api/client';

interface SkillListItem {
  name: string;
  description: string;
  triggers: string[];
  size_bytes: number;
  uploaded_at: number;
  signedUrl: string;
}

interface Props {
  /** Active persona's enabled_skills. When empty the row renders nothing. */
  enabledSkills: string[];
  /** Current textarea value — used to detect already-queued chips. */
  value: string;
  /** Called with the updated textarea value after inserting "/SkillName ". */
  onInsert: (next: string, caretPos: number) => void;
  /** Called on double-click: submit "/SkillName" standalone, as-is. */
  onInvokeNow: (slashText: string) => void;
}

/**
 * Stage 28: horizontal chip row rendered under the InputBar textarea. One
 * chip per skill the active persona has enabled; click to insert a slash
 * command into the draft, double-click to fire it as a standalone message.
 * Nothing renders when the persona has no enabled_skills — keeps the chat
 * surface uncluttered for users who haven't curated the list.
 */
export function SkillChipRow({
  enabledSkills,
  value,
  onInsert,
  onInvokeNow,
}: Props): JSX.Element | null {
  const [skills, setSkills] = useState<SkillListItem[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiGet<{ skills: SkillListItem[] }>('/api/skills');
        if (!cancelled) setSkills(res.skills);
      } catch {
        if (!cancelled) setSkills([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (enabledSkills.length === 0) return null;
  if (skills === null) return null;

  const available = skills.filter((s) =>
    enabledSkills.some((n) => n.toLowerCase() === s.name.toLowerCase()),
  );
  if (available.length === 0) return null;

  const handleClick = (name: string): void => {
    const slash = `/${name} `;
    const insertion = value.length === 0 ? slash : `${value} ${slash}`;
    onInsert(insertion, insertion.length);
  };

  return (
    <div
      className="skill-chip-row"
      role="toolbar"
      aria-label="Insert skill invocation"
      style={{
        display: 'flex',
        gap: 6,
        overflowX: 'auto',
        padding: '4px 0',
        borderTop: '1px solid var(--border, #333)',
      }}
    >
      {available.map((s) => {
        const slashToken = `/${s.name}`;
        const alreadyQueued = value.trimStart().startsWith(slashToken);
        return (
          <button
            key={s.name}
            type="button"
            className="skill-chip"
            aria-pressed={alreadyQueued}
            aria-label={`Invoke skill ${s.name}`}
            title={`${s.description}${
              s.triggers.length > 0
                ? `\n\nAuto-triggers: ${s.triggers.join(', ')}`
                : ''
            }\n\nClick to insert /${s.name}; double-click to send immediately.`}
            onClick={() => handleClick(s.name)}
            onDoubleClick={() => onInvokeNow(slashToken)}
            style={{
              flexShrink: 0,
              padding: '4px 10px',
              borderRadius: 999,
              border: '1px solid var(--border, #333)',
              background: alreadyQueued
                ? 'var(--accent, #3a6)'
                : 'var(--bg-subtle, #1a1a1a)',
              color: alreadyQueued ? 'white' : 'inherit',
              cursor: 'pointer',
              fontSize: '0.85em',
              whiteSpace: 'nowrap',
            }}
          >
            {slashToken}
          </button>
        );
      })}
    </div>
  );
}
